// 문자 발송 채널 API 클라이언트 — /api/v2/crm/messaging/*  (task 352 페어링 개편)
//
// 매장폰 연결(6자리 코드 페어링)·상태 조회와 테스트 발송. 백엔드 라우트
// server/src/v2/routes/crm/messaging.ts 와 매칭.

import { authJson } from "./_client";
import type { StorePhoneLineStatus } from "../../shared/store-phone-line";

const API_V2 = `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/v2`;

export type SendChannel = "manual" | "aligo" | "apk";

export interface DeviceStatus {
  connected: boolean; // 유효한(해제 안 된) 발송 폰이 있나
  online: boolean; // 최근 45초 내 폴링 신고
  name: string | null; // 폰 모델명
  lastSeenAt: string | null;
  smsGranted: boolean;
  /**
   * 전화 걸기 권한. **이 값으로 메뉴를 감추지 않는다** — 전화 메뉴는 항상 보이고,
   * 권한이 없으면 눌렀을 때 서버가 이유를 돌려준다(티켓 #293 후속 결정).
   */
  callGranted: boolean;
  /** 마지막 신고 시점에 그 폰이 통화 중이었나. */
  inCall: boolean;
  batteryExempt: boolean | null;
  appVersion: string | null;
  phoneNumber: string | null;
  reportedPhoneNumber: string | null;
  manuallyVerifiedPhoneNumber: string | null;
  phoneLineStatus: StorePhoneLineStatus;
  pairedAt: string | null; // 연결 마법사의 "코드 발급 이후 새 연결" 판정 기준
}

export interface SendingDevice extends DeviceStatus {
  id: string;
  label: string | null;
}

export async function fetchSendingDevices(): Promise<{ devices: SendingDevice[] }> {
  return authJson(`${API_V2}/crm/messaging/devices`);
}

/**
 * 매장폰으로 전화 걸기 (티켓 #293).
 *
 * 확인창 없이 곧바로 걸리는 게 요구사항이라, 걸 수 없는 상태는 서버가 409 와 함께 이유를
 * 돌려준다. 화면은 그 문장을 그대로 보여준다 — 조용히 실패하면 폰을 보러 가야 한다.
 */
export async function callFromStorePhone(
  phone: string,
  name?: string | null,
  targetDeviceId?: string | null,
): Promise<{ ok: boolean; queueId: string; duplicate: boolean }> {
  return authJson(`${API_V2}/crm/messaging/call`, {
    method: "POST",
    body: JSON.stringify({
      phone,
      name: name ?? undefined,
      targetDeviceId: targetDeviceId ?? undefined,
    }),
  });
}

export async function fetchDeviceStatus(): Promise<{
  device: DeviceStatus;
  aligoReady: boolean;
}> {
  return authJson(`${API_V2}/crm/messaging/device`);
}

/** 연결 코드 발급 — 추가/교체 선택은 코드에 박혀 앱이 실제 소진할 때 적용된다(10분 유효). */
export async function createPairing(input: {
  mode: "add" | "replace";
  replaceDeviceId?: string;
}): Promise<{
  ok: boolean;
  code: string;
  expiresAt: string;
}> {
  return authJson(`${API_V2}/crm/messaging/pairing`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** 연결 해제 — 기기 토큰 즉시 무효. 폰 앱은 다음 폴링에서 연결 끊김을 안다. */
export async function disconnectDevice(): Promise<{
  ok: boolean;
  device: DeviceStatus;
}> {
  return authJson(`${API_V2}/crm/messaging/device`, { method: "DELETE" });
}

export interface TestSendResult {
  ok: boolean;
  sent?: boolean;
  queued?: boolean;
  queueId?: string;
  deviceOnline?: boolean;
}

export async function testSend(
  phone: string,
  channel: Exclude<SendChannel, "manual">,
): Promise<TestSendResult> {
  return authJson(`${API_V2}/crm/messaging/test-send`, {
    method: "POST",
    body: JSON.stringify({ phone, channel }),
  });
}

export interface QueueResult {
  status: "queued" | "sending" | "sent" | "failed" | "canceled" | "missing";
  error: string | null;
  doneAt: string | null;
}

export async function fetchQueueResult(queueId: string): Promise<QueueResult> {
  return authJson(`${API_V2}/crm/messaging/queue/${queueId}`);
}
